/**
 * RBAC Permissions Configuration
 * Must match backend permissions exactly
 */

import { ROUTES } from '@/config/routes';

/** Roles the CODE grants access by name (engine domain/constants/platform.SystemRole). Department
 *  roles are data in the `roles` table. */
export const SystemRole = {
  SUPER_ADMIN: 'super_admin',
  CLIENT_ADMIN: 'client_admin',
  DEMO_USER: 'demo_user',
} as const;
export type SystemRole = (typeof SystemRole)[keyof typeof SystemRole];

// Department-member roles — get the member dashboard, chat, and KB browsing
const MEMBER_ROLES = ["hr_member", "legal_member", "finance_member"];

export const SECTION_PERMISSIONS: Record<string, string[]> = {
  platform: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN, SystemRole.DEMO_USER, ...MEMBER_ROLES],
  observability: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
  admin: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
};

export const PAGE_PERMISSIONS: Record<string, string[]> = {
  [ROUTES.DASHBOARD]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN, SystemRole.DEMO_USER, ...MEMBER_ROLES],
  [ROUTES.CHAT]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN, SystemRole.DEMO_USER, ...MEMBER_ROLES],
  [ROUTES.KNOWLEDGE_BASE]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN, SystemRole.DEMO_USER, ...MEMBER_ROLES],
  [ROUTES.ANALYTICS]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
  [ROUTES.USER_MANAGEMENT]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
  [ROUTES.KB_ACCESS]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
  [ROUTES.TENANT_CONFIGURATION]: [SystemRole.SUPER_ADMIN, SystemRole.CLIENT_ADMIN],
  [ROUTES.EVAL]: [SystemRole.SUPER_ADMIN],  // internal eval console — super_admin only
  // Platform screen ([BY4]). Health: super-admin only (owner 2026-10-11 — clients see the Dashboard
  // status instead). A client's AI model lives in Tenant Configuration (super-admin column).
  [ROUTES.HEALTH]: [SystemRole.SUPER_ADMIN],
};

export const DEFAULT_LANDING_PAGE = ROUTES.DASHBOARD;

export function hasSectionAccess(userRoles: string[] | undefined, section: string): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  if (userRoles.includes(SystemRole.SUPER_ADMIN)) return true;
  const allowedRoles = SECTION_PERMISSIONS[section.toLowerCase()] || [];
  return userRoles.some(role => allowedRoles.includes(role));
}

export function hasPageAccess(userRoles: string[] | undefined, pagePath: string): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  if (userRoles.includes(SystemRole.SUPER_ADMIN)) return true;
  const allowedRoles = PAGE_PERMISSIONS[pagePath] || [];
  return userRoles.some(role => allowedRoles.includes(role));
}
